const PGAdapter = require('../../common/pg-adapter')
const {
  selectFunderIdByCouchdbIdQuery,
  deleteFunderByCouchdbIdQuery,
  deleteFunderProgramPartnersByFunderIdQuery,
  updateChildFundersParentQuery,
  selectFundersByProgramsQuery
} = require('../queries')

const FUNDER_COLUMNS = [
  'id', 'couchdb_id', 'fsid', 'name',
  'parent_funder_id',
  'created_at', 'created_by', 'updated_at', 'updated_by'
]

class FunderPgAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    super(
      pgConnection,
      'avocado.data_funder',
      username,
      FUNDER_COLUMNS,
      'id',
      {},
      logger,
      { addUuid: false }
    )
  }

  async setDeleted (couchdbId, { client } = {}) {
    const db = client || this.pgConnection
    const deleteQuery = deleteFunderByCouchdbIdQuery(couchdbId)
    return db.query(deleteQuery.query, deleteQuery.values)
  }

  // Funders scoped to these program ids via the funder<->program-partner
  // mapping table, optionally narrowed to an explicit funder allowlist (the
  // user's `funders` list). Callers pass the programs the requesting user is
  // allowed to see — see the program module's tools.scopedProgramIds, which the
  // agave funder controller uses.
  async listForPrograms (programIds, { funderIds, client } = {}) {
    if (!programIds || !programIds.length) return []
    const db = client || this.pgConnection
    const { query, values } = selectFundersByProgramsQuery(programIds, { funderIds })
    const { rows } = await db.query(query, values)
    return rows
  }

  // A single funder, subject to the same program/allowlist scoping as
  // `listForPrograms` — narrowed in the statement rather than by reading every
  // row and filtering in JS. Returns undefined when the funder doesn't exist or
  // falls outside the caller's scope; the two are deliberately
  // indistinguishable, so a 404 doesn't confirm a funder the user can't read.
  async getForPrograms (funderId, programIds, { funderIds, client } = {}) {
    if (!programIds || !programIds.length) return undefined
    const db = client || this.pgConnection
    const { query, values } = selectFundersByProgramsQuery(programIds, {
      funderIds,
      funderId
    })
    const { rows } = await db.query(query, values)
    return rows[0]
  }

  async upsertFunder (funder, logger, { client } = {}) {
    const db = client || this.pgConnection
    return runInTransaction(db, async (txClient) => {
      const { programs = {}, ordersParentFunderId, ...funderRow } = funder

      logger.info({ code: 'upsert-start', couchdb_id: funderRow.couchdb_id })

      const existingQuery = selectFunderIdByCouchdbIdQuery(funderRow.couchdb_id)
      const { rows: existing } = await txClient.query(existingQuery.query, existingQuery.values)

      const funderId = existing.length > 0 ? String(existing[0].id) : funderRow.id
      const parentFunderId = await this._resolveParentFunderUuid(txClient, ordersParentFunderId)

      const deletePartnersQuery = deleteFunderProgramPartnersByFunderIdQuery(funderId)
      await txClient.query(deletePartnersQuery.query, deletePartnersQuery.values)

      const deleteFunderQuery = deleteFunderByCouchdbIdQuery(funderRow.couchdb_id)
      await txClient.query(deleteFunderQuery.query, deleteFunderQuery.values)

      await this._insertFunderRow(txClient, {
        ...funderRow,
        id: funderId,
        parent_funder_id: parentFunderId
      })

      await this._insertFunderProgramPartners(txClient, funderId, { programs })

      if (parentFunderId) {
        const updateParentQuery = updateChildFundersParentQuery(funderId, funderRow.couchdb_id)
        await txClient.query(updateParentQuery.query, updateParentQuery.values)
      }

      return funderId
    })
  }

  async _resolveParentFunderUuid (db, parentCouchdbId) {
    if (!parentCouchdbId) return null

    const selectQuery = selectFunderIdByCouchdbIdQuery(parentCouchdbId)
    const { rows } = await db.query(selectQuery.query, selectQuery.values)

    return rows.length > 0 ? String(rows[0].id) : null
  }

  async _insertFunderRow (db, row) {
    const values = FUNDER_COLUMNS.map(c => row[c])
    const placeholders = values.map((_, i) => `$${i + 1}`).join(', ')

    return db.query(
      `INSERT INTO avocado.data_funder (${FUNDER_COLUMNS.join(', ')}) VALUES (${placeholders}) RETURNING *`,
      values
    )
  }

  async _insertFunderProgramPartners (db, funderId, funder) {
    const rows = []

    for (const [programFsid, programData] of Object.entries(funder.programs || {})) {
      const implementingPartners = programData.implementingPartners || []
      const hideOnSettings = !!programData.hideOnSettings

      if (implementingPartners.length === 0) {
        rows.push({
          funder_id: funderId,
          program_fsid: programFsid,
          partner_id: null,
          partner_name: null,
          hide_on_settings: hideOnSettings
        })
        continue
      }

      for (const partner of implementingPartners) {
        rows.push({
          funder_id: funderId,
          program_fsid: programFsid,
          partner_id: partner._id || null,
          partner_name: partner.name || null,
          hide_on_settings: hideOnSettings
        })
      }
    }

    if (rows.length === 0) return []

    const columns = ['funder_id', 'program_fsid', 'partner_id', 'partner_name', 'hide_on_settings']
    const allValues = []
    const placeholders = []

    for (let i = 0; i < rows.length; i++) {
      const rowValues = columns.map(c => rows[i][c])
      allValues.push(...rowValues)
      placeholders.push(`(${rowValues.map((_, j) => `$${i * columns.length + j + 1}`).join(', ')})`)
    }

    return db.query(
      `INSERT INTO avocado.data_funder_program_partner (${columns.join(', ')}) VALUES ${placeholders.join(', ')} RETURNING *`,
      allValues
    )
  }
}

const runInTransaction = async (client, run) => {
  try {
    await client.query('BEGIN')
    const result = await run(client)
    await client.query('COMMIT')
    return result
  } catch (err) {
    await client.query('ROLLBACK')
    throw err
  }
}

module.exports = FunderPgAdapter
